import type { InvestmentItem, LiquidityBreakdown } from '@/types';

/**
 * Gates the availability-lens Perfil de Liquidez (VER-256). Default-off until the
 * fleet's syncs backfill gracePeriodDate/amountWithdrawal and telemetry shows sane
 * distribution rates; off preserves the pre-existing dueDate widget verbatim.
 */
export const LIQUIDITY_AVAILABILITY_FLAG = 'investments-liquidity-availability';

export type LiquidityBucketKey = 'daily' | 'short' | 'long' | 'unknown';

export const LIQUIDITY_BUCKET_LABELS: Record<LiquidityBucketKey, string> = {
  daily: 'Liquidez Imediata',
  short: 'Curto Prazo',
  long: 'Longo Prazo',
  unknown: 'Indeterminado',
};

export const LIQUIDITY_BUCKET_EXPLANATIONS: Record<LiquidityBucketKey, string> = {
  daily: 'Dinheiro que já pode ser usado: carência cumprida, recompra diária (Tesouro), negociação em bolsa ou valor liberado pelo banco.',
  short: 'Fica disponível em até 30 dias — fim da carência ou vencimento.',
  long: 'Fica disponível em mais de 30 dias, em geral só no vencimento.',
  unknown: 'O Open Finance não informou prazo de resgate nem valor disponível para este ativo.',
};

/**
 * Legacy (pre-VER-256) liquidity semantics, keyed by the original wire values so the
 * off-flag widget and its server-side filter behave exactly as before the availability
 * lens shipped. Delete this whole block when the flag is rolled out.
 */
export type LegacyLiquidityKey = 'immediate' | 'shortTerm' | 'longTerm';

export const LEGACY_LIQUIDITY_LABELS: Record<LegacyLiquidityKey, string> = {
  immediate: 'Liquidez Imediata',
  shortTerm: 'Curto Prazo',
  longTerm: 'Longo Prazo',
};

export const LEGACY_LIQUIDITY_SUBS: Record<LegacyLiquidityKey, string> = {
  immediate: 'D+0 / D+1',
  shortTerm: '< 1 ano',
  longTerm: '> 1 ano',
};

export function buildLegacyLiquidityProfile(positions: InvestmentItem[]): Record<LegacyLiquidityKey, number> {
  const today = new Date();
  const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  let immediate = 0, shortTerm = 0, longTerm = 0;
  positions.forEach(inv => {
    if (!inv.dueDate) {
      immediate += inv.balance;
    } else {
      const due = new Date(inv.dueDate);
      if (due <= in30Days) {
        shortTerm += inv.balance;
      } else {
        longTerm += inv.balance;
      }
    }
  });
  return { immediate, shortTerm, longTerm };
}

export function matchesLegacyLiquidity(inv: InvestmentItem, key: string): boolean {
  const today = new Date();
  const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const oneYear = new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000);
  if (key === 'immediate') return !inv.dueDate;
  if (!inv.dueDate) return key !== 'shortTerm' && key !== 'longTerm';
  const due = new Date(inv.dueDate);
  if (key === 'shortTerm') return due > today && due <= in30Days;
  if (key === 'longTerm') return due > oneYear;
  return true;
}

export function isAvailabilityBucketKey(key: string): key is LiquidityBucketKey {
  return key in LIQUIDITY_BUCKET_LABELS;
}

export function liquidityFilterLabel(key: string): string {
  return (
    LIQUIDITY_BUCKET_LABELS[key as LiquidityBucketKey]
    ?? LEGACY_LIQUIDITY_LABELS[key as LegacyLiquidityKey]
    ?? key
  );
}

/**
 * Telemetry payload for `investments.liquidity_distribution` — shares (of balance,
 * 3 decimal places) and counts only, never amounts. Emitted whatever the flag state:
 * its purpose is to measure the real Indeterminado/settlement-unknown rates before
 * the flag flips.
 */
export function buildLiquidityDistributionPayload(liquidity: LiquidityBreakdown) {
  const { daily, short, long, unknown } = liquidity;
  const totalValue = daily.value + short.value + long.value + unknown.value;
  const share = (value: number) => (totalValue > 0 ? Math.round((value / totalValue) * 1000) / 1000 : 0);
  return {
    daily_share: share(daily.value),
    short_share: share(short.value),
    long_share: share(long.value),
    unknown_share: share(unknown.value),
    daily_count: daily.count,
    short_count: short.count,
    long_count: long.count,
    unknown_count: unknown.count,
    total_count: daily.count + short.count + long.count + unknown.count,
    settlement_unknown_count: liquidity.settlementUnknownCount,
    partial_availability_count: liquidity.partialAvailabilityCount,
    blocked_contradiction_count: liquidity.blockedContradictionCount,
  };
}
